import { Injectable, OnDestroy, inject } from "@angular/core";
import { Observable, Subject, forkJoin, of } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { SpatialService } from "~app/common/components/map/services/spatial.service";
import { Subtyp1, Subtyp2 } from "~app/common/components/map/symbols/kartgeometri.constants";
import { EntityDTO } from "~app/common/models/entity-models";
import { RuleErrorContext, RuleSeverityLevel, RuleValidationMessage, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Avverkningsanmalan, VKAvverkningsanmalanDTO } from "../../models/avverkningsanmalan-models";
import { Geometri } from "../../models/karta-models";
import { AvtalService } from "../avtal.service";
import { KontraktMediatorService } from "../kontrakt-mediator.service";
import { VkKartaMediatorService } from "../vk-karta-mediator.service";
import { VirkeskopValidationService } from "./virkeskop-validation.service";

@Injectable()
export class GeometryClientValidationService implements OnDestroy {
    private readonly vkKartaMediatorService = inject(VkKartaMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly spatialService = inject(SpatialService);
    private readonly avtalService = inject(AvtalService);

    private readonly avverkningsanmalningar: Avverkningsanmalan[] = [];
    private readonly destroy$ = new Subject<void>();

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    initSubscriptions() {
        this.vkKartaMediatorService.kontraktGeometriChanged$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(changed => {
            this.updateGeometriRules(changed);
        });

        this.kontraktMediatorService.atgarderPreload$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(atgarder => {
            this.initAvverkningsanmalningar(atgarder);
        });
    }

    private get kontrakt() {
        return this.kontraktMediatorService.kontrakt;
    }

    private updateGeometriRules(object: Geometri) {
        if (!object) {
            return;
        }

        this.validationService.updateClientSideValidation(
            object?.kartgeometri?.subtyp1 === Subtyp1.hansynYta &&
            object?.kartgeometri?.persistedKartgeometri &&
            object?.kartgeometri.geometri !== object?.kartgeometri?.persistedKartgeometri?.geometri &&
            this.isHansynKulturmiljoChangeAffected(object),
            this.createValidationMessage({
                messageId: "hansynsytaDecreased",
                message: "Du har gjort ändringar gällande hänsyn. Överväg om avverkningsanmälan behöver kompletteras eller förnyas.",
                severity: RuleSeverityLevel.warning
            })
        );

        this.validationService.updateClientSideValidation(
            object?.kartgeometri?.subtyp1 === Subtyp1.hansynYta &&
            object?.kartgeometri.subtyp2 === Subtyp2.kulturMiljo &&
            object?.kartgeometri?.isNew &&
            this.isHansynKulturmiljoChangeAffected(object),
            this.createValidationMessage({
                messageId: "kartgeometriNy",
                message: "Ett hänsyn av typen Kulturmiljö har lagts till. Överväg om ny avverkningsanmälan krävs.",
                severity: RuleSeverityLevel.warning
            })
        );
    }

    private createValidationMessage(message: Partial<RuleValidationMessage>): RuleValidationMessage {
        return {
            severity: RuleSeverityLevel.warning,
            strategy: RuleVisibilityStrategy.preSave,
            context: RuleErrorContext.save,
            entityType: "kontrakt",
            entityGuids: [this.kontrakt.guid],
            message: "",
            messageId: "",
            ...message
        };
    }

    private initAvverkningsanmalningar(atgarder: EntityDTO[]) {
        const obs: Observable<VKAvverkningsanmalanDTO>[] = [];
        atgarder.forEach(atg => {
            const observable = !ObjectUtils.isNullOrUndefined(atg.id)
                ? this.avtalService.getAvverkningsanmalanForAtgard(atg.id)
                : of(null);
            obs.push(observable);
        });
        forkJoin(obs).subscribe(dtos => {
            dtos.forEach(avan => {
                this.avverkningsanmalningar.push(new Avverkningsanmalan(avan));
            });
        });
    }

    private isHansynKulturmiljoChangeAffected(geometri: Geometri): boolean {
        if ((geometri.kartgeometri.subtyp1 !== Subtyp1.hansynYta && geometri.kartgeometri.subtyp1 !== Subtyp1.hansynPunkt) ||
            geometri.kartgeometri.subtyp2 !== Subtyp2.kulturMiljo) {
            return false;
        }

        if (!this.isAnyAvverkningsanmalanGjord()) {
            return false;
        }

        if (geometri.kartgeometri.subtyp1 === Subtyp1.hansynYta && ObjectUtils.hasValue(geometri.kartgeometri.persistedKartgeometri)) {
            if (geometri.kartgeometri.subtyp2 === Subtyp2.kulturMiljo && geometri.kartgeometri.persistedKartgeometri.subtyp2 !== Subtyp2.kulturMiljo) {
                return true;
            }
            const ha = this.spatialService.getAreaDifference(geometri.kartgeometri.geometri, geometri.kartgeometri.persistedKartgeometri.geometri);
            return ha < 0;
        }
        return false;
    }

    private isAnyAvverkningsanmalanGjord(): boolean {
        return this.avverkningsanmalningar.some(x => x.isAvverkningsanmalanGjord);
    }
}
